/**
 * CALIBRAR MODELOS — uso interno (administrador).
 *
 * Rota não divulgada ao cliente final: /calibrar-modelos
 * A peça é FIXA (mockup mestre, sem qualquer alteração). Só a camada de cima é
 * movida, escalada e rotacionada.
 *
 * Dois modos, porque o que se calibra é guardado em lugares diferentes:
 * a GOLA tem um encaixe por MODELAGEM × VISTA (é a mesma arte encaixando em
 * golas de tamanhos diferentes); qualquer OUTRA CAMADA — escudo, nome, número,
 * patrocínio — tem um encaixe só, gravado na própria camada.
 *
 * E dois TIPOS de trabalho, que são coisas diferentes:
 *
 * ÚNICO — escolhe-se uma camada e só ela aparece. É o ajuste fino: nada mais
 * na tela para distrair, e o palco segue a camada (vista, modelagem e peça).
 *
 * COMPLETO — escolhe-se a PEÇA e aparecem TODAS as camadas dela ao mesmo
 * tempo; clicar em uma seleciona-a para editar, sem que as outras sumam. É o
 * que falta no ajuste um por um: nome, número e escudo podem estar cada um
 * perfeito sozinho e colidindo entre si, e isso só se vê com tudo junto.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Toaster, toast } from "sonner";

import type { CollarFace, CollarTransform } from "@/data/collars";

import {
  exportCollarOverrides,
  getCollarTransform,
  resetCollarTransform,
  saveCollarTransform,
} from "@/lib/collar-presets";
import {
  allLayers,
  useCollars,
  useFits,
  useMockupIndex,
  useOptionGroups,
  usePieces,
  useSaveCollarPreset,
  useSaveLayerBox,
  type LayerRef,
} from "@/lib/catalog-admin";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/calibrar-modelos")({
  /** `?camada=<id>` chega do editor de camadas, já com a camada escolhida. */
  validateSearch: (search: Record<string, unknown>): { camada?: string } => {
    const value = search["camada"];
    return typeof value === "string" && value ? { camada: value } : {};
  },
  head: () => ({
    meta: [
      { title: "Calibrar modelos — painel interno" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Painel interno de calibração do encaixe das golas sobre os mockups mestres das camisetas.",
      },
      { property: "og:title", content: "Calibrar modelos — painel interno" },
      {
        property: "og:description",
        content: "Ajuste fino do encaixe das camadas sobre os mockups mestres.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CalibrarGola,
});

const FIELDS = [
  { key: "x", label: "X (%)", min: 0, max: 100, step: 0.1 },
  // Até 100: o topo da camada pode ir ao pé do palco. Parar em 60 servia à
  // gola, que mora no alto da camisa, e impedia descer o número e o escudo na
  // perna do calção.
  { key: "y", label: "Y (%)", min: -20, max: 100, step: 0.1 },
  { key: "width", label: "Escala / largura (%)", min: 5, max: 90, step: 0.1 },
  { key: "rotation", label: "Rotação (°)", min: -30, max: 30, step: 0.1 },
] as const;

/**
 * Em camada de TEXTO a "largura" é o tamanho da fonte.
 *
 * O controle é o mesmo — arrastar um número até a arte encaixar — mas o que
 * ele grava muda: largura não escala texto, tamanho de fonte sim. Trocar só o
 * rótulo e a faixa evita duplicar a tela inteira por causa de um campo.
 */
const TEXT_SIZE_FIELD = {
  key: "width",
  label: "Tamanho do texto (% da altura)",
  min: 1,
  max: 40,
  step: 0.1,
} as const;

/** Amostra desenhada na calibragem — o cliente digita o texto de verdade. */
const SAMPLE = { number: "10", text: "SILVA" };

const round = (v: number) => Math.round(v * 100) / 100;

type Mode = "gola" | "camada";

/** ÚNICO = uma camada por vez; COMPLETO = a peça inteira, clicável. */
type Scope = "unico" | "full";

/**
 * Uma coisa desenhada no palco.
 *
 * Gola e camada viram o mesmo item de propósito: o palco não precisa saber
 * onde cada encaixe é GRAVADO (preset da gola x coluna da camada) para
 * desenhá-lo — só na hora de salvar isso importa.
 */
interface StageItem {
  key: string;
  label: string;
  /** A camada do catálogo; null = a gola, cujo encaixe vive no preset. */
  ref: LayerRef | null;
  box: CollarTransform;
  /** Arte a desenhar; ausente em texto e em arte enviada pelo cliente. */
  src: string | undefined;
}

/**
 * Texto de amostra de uma camada — o cliente digita o de verdade.
 *
 * Sai do TIPO do grupo, e não de um palpite: adivinhar pela presença de
 * `fontSize` fazia o nome nas costas aparecer como "10", porque toda camada de
 * texto tem tamanho de fonte.
 */
const sampleFor = (ref: LayerRef | null): string =>
  ref?.groupType === "NUMBER" || ref?.groupType === "COUNTER" ? SAMPLE.number : SAMPLE.text;

/** Encaixe gravado de uma camada, no formato do palco. */
const boxOf = (ref: LayerRef): CollarTransform => ({
  x: ref.layer.x ?? 50,
  y: ref.layer.y ?? 0,
  width: ref.layer.kind === "TEXT" ? (ref.layer.fontSize ?? 6) : (ref.layer.width ?? 100),
  rotation: ref.layer.rotation ?? 0,
});

const labelOf = (ref: LayerRef): string =>
  `${ref.groupLabel}${ref.choiceLabel ? ` · ${ref.choiceLabel}` : ""}`;

function CalibrarGola() {
  // Golas, modelagens e camadas vêm do cadastro: calibrar algo criado no
  // painel não pode depender de alguém editar uma lista no código.
  const collars = useCollars();
  const fits = useFits();
  const mockups = useMockupIndex();
  const groups = useOptionGroups();
  const pieceRows = usePieces();
  const savePreset = useSaveCollarPreset();
  const saveBox = useSaveLayerBox();

  const { camada } = Route.useSearch();

  const [mode, setMode] = useState<Mode>(camada ? "camada" : "gola");
  // Chegar por `?camada=` é pedido de ajuste fino daquela camada: abrir no
  // modo completo mostraria a peça inteira e esconderia o que se veio ver.
  const [scope, setScope] = useState<Scope>("unico");
  const [layerId, setLayerId] = useState(camada ?? "");
  const [pieceId, setPieceId] = useState("");
  const [fit, setFit] = useState("");
  const [face, setFace] = useState<CollarFace>("front");
  const [collarId, setCollarId] = useState("");
  const [opacity, setOpacity] = useState(1);
  const [guides, setGuides] = useState(true);
  const [transform, setTransform] = useState<CollarTransform>({
    x: 50,
    y: 5.2,
    width: 30,
    rotation: 0,
  });
  const stageRef = useRef<HTMLDivElement>(null);

  const collarRows = useMemo(
    () => (collars.data ?? []).filter((row) => row.active),
    [collars.data],
  );
  const fitRows = useMemo(() => fits.data ?? [], [fits.data]);

  // Primeira gola e primeira modelagem assim que a lista chega.
  useEffect(() => {
    if (!collarId && collarRows[0]) setCollarId(collarRows[0].id);
  }, [collarId, collarRows]);
  useEffect(() => {
    if (!fit && fitRows[0]) setFit(fitRows[0].id);
  }, [fit, fitRows]);

  const collar = collarRows.find((row) => row.id === collarId);

  // Camadas de todos os grupos, achatadas — é a lista do modo "camada".
  const layers = useMemo(() => allLayers(groups.data ?? []), [groups.data]);
  const layerRef: LayerRef | undefined =
    layers.find((item) => item.layer.id === layerId) ?? layers[0];

  useEffect(() => {
    if (!layerId && layerRef?.layer.id) setLayerId(layerRef.layer.id);
  }, [layerId, layerRef]);

  // Em TEXTO os controles mudam de significado: a largura vira tamanho de
  // fonte, e o que se grava e outro campo.
  const layerKind = mode === "camada" ? (layerRef?.layer.kind ?? "IMAGE") : "IMAGE";
  const overlayLabel =
    mode === "gola"
      ? (collar?.label ?? "")
      : `${layerRef?.groupLabel ?? ""}${layerRef?.choiceLabel ? ` · ${layerRef.choiceLabel}` : ""}`;

  // O palco é o mockup real da peça: calibrar uma gola para uma modelagem
  // recém-criada exige ver a arte dessa modelagem, não uma lista fixa.
  // `?? {}` cria objeto novo a cada render e invalidaria o useMemo abaixo.
  const index = useMemo(() => mockups.data ?? {}, [mockups.data]);
  const pieces = useMemo(
    () => Object.keys(index).filter((id) => Object.keys(index[id] ?? {}).length > 0),
    [index],
  );

  /**
   * Peças oferecidas no seletor.
   *
   * Calibrando a GOLA sozinha, só entram as peças que usam o grupo de golas:
   * calção, bermuda e meião não têm gola, e escolher um deles punha a arte da
   * gola sobre uma peça em que ela nunca é desenhada. Sem nenhuma peça ligada
   * ao grupo, a lista inteira continua — melhor que um seletor vazio.
   */
  const collarPieces = useMemo(() => {
    const ids = new Set(
      (groups.data ?? [])
        .filter((row) => row.source === "COLLARS")
        .flatMap((row) => row.usedBy.map((piece) => piece.id)),
    );
    const withCollar = pieces.filter((id) => ids.has(id));
    return withCollar.length > 0 ? withCollar : pieces;
  }, [groups.data, pieces]);
  const pieceOptions = scope === "unico" && mode === "gola" ? collarPieces : pieces;
  // A peça escolhida em outro modo pode não existir nesta lista (um calção, ao
  // voltar para Gola): cai na primeira que serve em vez de manter a errada.
  const currentPiece = pieceOptions.includes(pieceId) ? pieceId : (pieceOptions[0] ?? "");

  /**
   * O palco segue a CAMADA escolhida.
   *
   * Cada camada ja sabe onde vive: a vista (`face`), a modelagem (`fit`) e as
   * pecas que usam o grupo. Escolher "numero — costas" e continuar vendo a
   * frente da camiseta, ou escolher o numero do calcao e calibra-lo sobre uma
   * camiseta, e ajustar coordenadas contra a arte errada — o encaixe sai certo
   * na tela e errado na loja. Trocar sozinho remove esse erro inteiro.
   *
   * Peca so troca quando a atual NAO usa o grupo: se a camada vale para
   * camiseta, blusa e regata, quem esta calibrando escolhe em qual delas olhar,
   * e trocar isso a cada clique seria roubar a escolha.
   */
  useEffect(() => {
    // No modo completo quem manda é a peça escolhida: mexer na vista ou na
    // modelagem a cada clique tiraria do lugar justamente o conjunto que se
    // quer olhar inteiro.
    if (scope !== "unico" || mode !== "camada" || !layerRef) return;

    const layerFace = layerRef.layer.face;
    if (layerFace) setFace(layerFace === "BACK" ? "back" : "front");
    if (layerRef.layer.fit) setFit(layerRef.layer.fit);

    const usable = layerRef.pieces.map((piece) => piece.id);
    setPieceId((current) => {
      const showing = current || pieces[0] || "";
      if (usable.length === 0 || usable.includes(showing)) return current;
      return usable.find((id) => pieces.includes(id)) ?? current;
    });
  }, [scope, mode, layerRef, pieces]);

  /** A peça usa gola? (o grupo derivado do cadastro de golas). */
  const usesCollar = (groups.data ?? []).some(
    (row) => row.source === "COLLARS" && row.usedBy.some((piece) => piece.id === currentPiece),
  );

  /**
   * Arte da gola NESTA vista — sem substituto.
   *
   * REGRA ABSOLUTA do projeto: a vista de costas só recebe camada de gola
   * quando existe arte PRÓPRIA de costas; nada é girado, espelhado ou
   * emprestado da frente. A loja obedece isso (a API nem emite a camada), e
   * emprestar a arte da frente aqui era pior do que palco vazio: desenhava uma
   * gola inteira que o cliente nunca vê, e convidava a calibrar o encaixe
   * contra uma arte que não é a daquela vista.
   */
  const collarSrc = face === "back" ? collar?.backUrl : collar?.frontUrl;

  /**
   * Tudo que a peça desenha nesta vista.
   *
   * Camada SEM ENCAIXE fica de fora: ela ocupa os 800×800 inteiros, cobriria
   * todas as outras e engoliria os cliques. Ela continua calibrável no modo
   * único, e o painel diz quantas ficaram de fora em vez de omitir em silêncio.
   */
  const pieceItems = useMemo((): StageItem[] => {
    const items: StageItem[] = [];

    if (usesCollar && collar && collarSrc && fit) {
      items.push({
        key: `gola:${collar.id}`,
        label: collar.label,
        ref: null,
        box: getCollarTransform(collar.id, fit, face),
        src: collarSrc,
      });
    }

    for (const item of layers) {
      if (!item.pieces.some((piece) => piece.id === currentPiece)) continue;
      // Camada de um lado só não aparece no outro; sem lado, aparece nos dois.
      if (item.layer.face && item.layer.face !== (face === "back" ? "BACK" : "FRONT")) continue;
      if (item.layer.fit && item.layer.fit !== fit) continue;
      if (item.layer.kind === "IMAGE" && item.layer.x === null && item.layer.width === null) {
        continue;
      }

      items.push({
        key: `camada:${item.layer.id}`,
        label: labelOf(item),
        ref: item,
        box: boxOf(item),
        ...(item.layer.kind === "IMAGE"
          ? { src: item.layer.image ?? undefined }
          : { src: undefined }),
      });
    }

    return items;
  }, [layers, currentPiece, face, fit, usesCollar, collar, collarSrc]);

  /** Camadas da peça que a vista completa não consegue mostrar. */
  const unplacedCount = layers.filter(
    (item) =>
      item.pieces.some((piece) => piece.id === currentPiece) &&
      item.layer.kind === "IMAGE" &&
      item.layer.x === null &&
      item.layer.width === null,
  ).length;

  /** O item que os controles estão editando. */
  const selectedKey = mode === "gola" ? `gola:${collarId}` : `camada:${layerRef?.layer.id ?? ""}`;

  /** Clicar no palco escolhe o que editar — sem tirar o resto da tela. */
  const selectItem = (item: StageItem) => {
    if (item.ref) {
      setMode("camada");
      setLayerId(item.ref.layer.id ?? "");
      return;
    }
    setMode("gola");
  };

  /**
   * O que está no palco.
   *
   * No modo único é só o alvo — o resto sairia do caminho do ajuste fino. No
   * completo é a peça inteira, e o alvo é um dos itens.
   */
  const stageItems: StageItem[] =
    scope === "full"
      ? pieceItems
      : mode === "gola"
        ? collar && collarSrc && fit
          ? [
              {
                key: selectedKey,
                label: collar.label,
                ref: null,
                box: getCollarTransform(collar.id, fit, face),
                src: collarSrc,
              },
            ]
          : []
        : layerRef
          ? [
              {
                key: selectedKey,
                label: labelOf(layerRef),
                ref: layerRef,
                box: boxOf(layerRef),
                ...(layerRef.layer.kind === "IMAGE"
                  ? { src: layerRef.layer.image ?? undefined }
                  : { src: undefined }),
              },
            ]
          : [];

  /** "Calção" em vez de "calcao" — o seletor virou navegação, nao debug. */
  const pieceName = (id: string): string =>
    (pieceRows.data ?? []).find((row) => row.id === id)?.name ?? id;
  const set = index[currentPiece]?.[fit];
  const baseSrc = face === "back" ? set?.back : set?.front;

  // Ao trocar a combinação, carrega o encaixe correspondente.
  //
  // Camada sem encaixe abre em tela cheia (centro, topo, 100% de largura), que
  // é exatamente como ela está sendo desenhada hoje — o ajuste começa de onde
  // a arte realmente está, e não de um valor inventado.
  useEffect(() => {
    if (mode === "gola") {
      if (!collarId || !fit) return;
      setTransform(getCollarTransform(collarId, fit, face));
      return;
    }
    const box = layerRef?.layer;
    if (!box) return;
    setTransform({
      x: box.x ?? 50,
      y: box.y ?? 0,
      width: box.kind === "TEXT" ? (box.fontSize ?? 6) : (box.width ?? 100),
      rotation: box.rotation ?? 0,
    });
  }, [mode, collarId, fit, face, layerRef]);

  const update = (key: keyof CollarTransform, value: number) =>
    setTransform((t) => ({ ...t, [key]: round(value) }));

  // Setas: mover. SHIFT = passo maior. ALT = ultrafino.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, [keyof CollarTransform, number]> = {
        ArrowLeft: ["x", -1],
        ArrowRight: ["x", 1],
        ArrowUp: ["y", -1],
        ArrowDown: ["y", 1],
      };
      const hit = map[e.key];
      if (!hit) return;
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
      e.preventDefault();
      const step = e.shiftKey ? 1 : e.altKey ? 0.02 : 0.1;
      setTransform((t) => ({
        ...t,
        [hit[0]]: round(t[hit[0]] + hit[1] * step),
      }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /**
   * Salva no banco e no rascunho local.
   *
   * O rascunho mantém o preview desta tela respondendo na hora; o banco é o
   * que faz o encaixe valer para o cliente. Gravar só localmente deixaria a
   * loja desenhando a gola na posição padrão para todo mundo.
   */
  const save = () => {
    if (mode === "camada") {
      if (!layerRef?.layer.id) return;
      const { width, ...position } = transform;
      saveBox.mutate(
        {
          layerId: layerRef.layer.id,
          ...position,
          ...(layerKind === "TEXT" ? { fontSize: width } : { width }),
        },
        {
          onSuccess: () => toast.success(`Encaixe salvo — ${overlayLabel}`),
          onError: (cause) => toast.error(errorMessage(cause)),
        },
      );
      return;
    }

    if (!collar || !fit) return;
    saveCollarTransform(collarId, fit, face, transform);
    savePreset.mutate(
      { collarId, fit, face, ...transform },
      {
        onSuccess: () =>
          toast.success(
            `Encaixe salvo — ${collar.label} · ${
              fitRows.find((row) => row.id === fit)?.label ?? fit
            } · ${face === "front" ? "Frente" : "Costas"}`,
          ),
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );
  };

  const reset = () => {
    if (mode === "camada") {
      // Camada não tem preset de fábrica: "restaurar" é voltar ao que está
      // gravado, desfazendo o arraste que ainda não foi salvo.
      const box = layerRef?.layer;
      if (box) {
        setTransform({
          x: box.x ?? 50,
          y: box.y ?? 0,
          width: box.kind === "TEXT" ? (box.fontSize ?? 6) : (box.width ?? 100),
          rotation: box.rotation ?? 0,
        });
      }
      toast.info("Voltou ao encaixe salvo.");
      return;
    }
    resetCollarTransform(collarId, fit, face);
    setTransform(getCollarTransform(collarId, fit, face));
    toast.info("Preset restaurado para o padrão.");
  };

  const json = useMemo(
    () => JSON.stringify(exportCollarOverrides(), null, 2),
    [transform, collarId, fit, face],
  );

  // A tela só some quando não há NADA cadastrado para calibrar. Uma gola sem
  // arte de costas não é isso: é uma combinação legítima que simplesmente não
  // desenha, e trocar a tela inteira por "cadastre uma gola" faria parecer que
  // o cadastro sumiu — o palco explica no lugar.
  const nothingToDraw = mode === "camada" && scope === "unico" ? !layerRef : !collar;

  return nothingToDraw || !baseSrc ? (
    <div className="grid min-h-screen place-items-center bg-background px-4 text-center text-sm text-muted-foreground">
      <Toaster richColors position="top-center" />
      {/* Fora do painel (para o palco ter a tela toda): a volta é por aqui. */}
      <Link
        to="/admin/catalogo"
        className="absolute left-4 top-4 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary sm:left-6 sm:top-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar ao catálogo
      </Link>
      {collars.isLoading || fits.isLoading || groups.isLoading
        ? "Carregando…"
        : mode === "gola"
          ? "Cadastre uma gola e uma peça com arte de modelagem para calibrar."
          : "Cadastre uma camada com arte em algum grupo de opção para calibrar."}
    </div>
  ) : (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6">
      <Toaster richColors position="top-center" />
      <div className="mx-auto max-w-6xl">
        <header className="mb-6">
          <Link
            to="/admin/catalogo"
            className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao catálogo
          </Link>
          <p className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
            Modo administrador
          </p>
          <h1 className="font-display text-3xl font-bold text-primary">Calibrar modelos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A peça é fixa. Ajuste apenas posição, escala e rotação da camada de cima. Setas movem
            (SHIFT = passo maior, ALT = ultrafino).
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
          {/* Palco: MESMO container para camiseta e gola */}
          <div
            ref={stageRef}
            className="relative aspect-square overflow-hidden rounded-3xl border bg-card shadow-card"
          >
            <img
              src={baseSrc}
              alt="Camiseta base"
              draggable={false}
              className="absolute inset-0 h-full w-full object-contain select-none"
            />
            {stageItems.map((item) => (
              <StageArt
                key={item.key}
                item={item}
                // O item selecionado desenha o encaixe QUE ESTÁ SENDO
                // ajustado; os outros, o que está gravado.
                box={item.key === selectedKey ? transform : item.box}
                selected={item.key === selectedKey}
                opacity={item.key === selectedKey ? opacity : 1}
                guides={guides}
                {...(scope === "full" ? { onSelect: () => selectItem(item) } : {})}
              />
            ))}

            {stageItems.length === 0 && (
              <p className="absolute inset-x-6 bottom-6 rounded-2xl bg-card/90 px-4 py-3 text-center text-xs text-muted-foreground">
                {mode === "gola" && scope === "unico" && face === "back"
                  ? `${collar?.label ?? "Esta gola"} não tem arte de costas — na loja a vista de costas não recebe camada de gola. Cadastre a arte das costas para calibrá-la aqui.`
                  : "Nenhuma camada com encaixe nesta peça e nesta vista."}
              </p>
            )}

            {guides && (
              <span className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-primary/40" />
            )}
          </div>

          {/* Controles */}
          <aside className="space-y-5 rounded-3xl border bg-card p-5 shadow-card">
            <Segment
              label="Tipo"
              value={scope}
              options={[
                { id: "unico", label: "Único" },
                { id: "full", label: "Completo" },
              ]}
              onChange={(v) => setScope(v as Scope)}
            />
            {/* No completo não se escolhe ENTRE gola e camada: as duas estão
                na tela, e o que se edita sai do clique. */}
            {scope === "unico" && (
              <Segment
                label="O que calibrar"
                value={mode}
                options={[
                  { id: "gola", label: "Gola" },
                  { id: "camada", label: "Camada" },
                ]}
                onChange={(v) => setMode(v as Mode)}
              />
            )}
            <Segment
              label="Peça"
              value={currentPiece}
              options={pieceOptions.map((id) => ({ id, label: pieceName(id) }))}
              onChange={setPieceId}
            />
            <Segment
              label="Modelagem"
              value={fit}
              options={fitRows.map((row) => ({ id: row.id, label: row.label }))}
              onChange={setFit}
            />
            <Segment
              label="Vista"
              value={face}
              options={[
                { id: "front", label: "Frente" },
                { id: "back", label: "Costas" },
              ]}
              onChange={(v) => setFace(v as CollarFace)}
            />
            {scope === "full" && (
              <div>
                <p className="mb-2 text-xs font-bold tracking-wide text-muted-foreground uppercase">
                  Camadas da peça
                </p>
                <div className="flex flex-col gap-1.5">
                  {stageItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => selectItem(item)}
                      className={cn(
                        "rounded-xl border px-3 py-2 text-left text-[11px] font-semibold transition-all",
                        item.key === selectedKey
                          ? "border-primary bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                {unplacedCount > 0 && (
                  <p className="mt-2 text-[11px] leading-4 text-muted-foreground">
                    {unplacedCount} camada(s) sem encaixe não aparecem aqui — ocupam a arte inteira.
                    Calibre no tipo Único.
                  </p>
                )}
              </div>
            )}

            {scope === "unico" && mode === "camada" && (
              <div>
                <p className="mb-2 text-xs font-bold tracking-wide text-muted-foreground uppercase">
                  Camada
                </p>
                <select
                  value={layerRef?.layer.id ?? ""}
                  onChange={(e) => setLayerId(e.target.value)}
                  className="w-full rounded-xl border px-2 py-2 text-xs"
                >
                  {layers.map((item) => (
                    <option key={item.layer.id} value={item.layer.id}>
                      {layerOptionLabel(item)}
                    </option>
                  ))}
                </select>
                {layerRef && layerRef.pieces.length > 0 && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    Usada em: {layerRef.pieces.map((piece) => piece.name).join(", ")}.
                  </p>
                )}
              </div>
            )}

            {(scope === "full" || mode === "gola") && usesCollar && (
              <div>
                <p className="mb-2 text-xs font-bold tracking-wide text-muted-foreground uppercase">
                  Gola
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {collarRows.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCollarId(c.id)}
                      className={cn(
                        "rounded-2xl border p-2 text-left text-[11px] font-semibold transition-all",
                        collarId === c.id
                          ? "border-primary bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <img
                        src={c.frontUrl}
                        alt={c.label}
                        className="mb-1 h-10 w-full object-contain"
                      />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {FIELDS.map((raw) => {
              const f = layerKind === "TEXT" && raw.key === "width" ? TEXT_SIZE_FIELD : raw;
              return (
                <div key={f.key}>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-primary">{f.label}</label>
                    <input
                      type="number"
                      step={f.step}
                      value={transform[f.key]}
                      onChange={(e) => update(f.key, Number(e.target.value))}
                      className="w-24 rounded-lg border px-2 py-1 text-right text-xs"
                    />
                  </div>
                  <input
                    type="range"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={transform[f.key]}
                    onChange={(e) => update(f.key, Number(e.target.value))}
                    className="mt-1 w-full accent-primary"
                  />
                </div>
              );
            })}

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-primary">
                  Opacidade da camada (só na calibração)
                </label>
                <span className="text-xs font-semibold text-muted-foreground">
                  {Math.round(opacity * 100)}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={opacity}
                onChange={(e) => setOpacity(Number(e.target.value))}
                className="mt-1 w-full accent-primary"
              />
            </div>

            <label className="flex items-center gap-2 text-xs font-semibold text-primary">
              <input
                type="checkbox"
                checked={guides}
                onChange={(e) => setGuides(e.target.checked)}
              />
              Guias de alinhamento
            </label>

            <div className="flex gap-2">
              <button
                onClick={save}
                className="flex-1 rounded-xl bg-primary px-3 py-2.5 text-xs font-bold text-primary-foreground transition-all hover:opacity-90 active:scale-95"
              >
                Salvar encaixe
              </button>
              <button
                onClick={reset}
                className="rounded-xl border px-3 py-2.5 text-xs font-bold text-primary transition-all hover:bg-muted active:scale-95"
              >
                Restaurar
              </button>
            </div>

            {mode === "gola" && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer font-bold text-primary">
                  Exportar presets salvos
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-muted p-2 text-[10px]">
                  {json}
                </pre>
              </details>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Segment({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold tracking-wide text-muted-foreground uppercase">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-bold transition-all active:scale-95",
              value === o.id
                ? "bg-primary text-primary-foreground"
                : "border text-primary hover:bg-primary/10",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Como uma camada aparece na lista.
 *
 * A VISTA e as PECAS entram no rotulo porque sem elas o numero da camisa
 * aparece duas vezes como "Numero — texto do cliente": mesma origem, encaixes
 * diferentes. Escolher a linha errada nao da erro nenhum — so calibra a arte
 * errada, e o defeito so aparece na loja.
 */
function layerOptionLabel(item: LayerRef): string {
  const face =
    item.layer.face === "BACK"
      ? "Costas"
      : item.layer.face === "FRONT"
        ? "Frente"
        : "Frente e costas";

  const content =
    item.layer.kind === "IMAGE"
      ? (item.layer.image?.split("/").pop() ?? "sem arte")
      : item.layer.kind === "TEXT"
        ? "texto do cliente"
        : "arte do cliente";

  const pieces = item.pieces.map((piece) => piece.name).join(", ");
  const missing = item.layer.kind === "IMAGE" && item.layer.x === null ? " (sem encaixe)" : "";

  return (
    [
      `${item.groupLabel}${item.choiceLabel ? ` · ${item.choiceLabel}` : ""}`,
      face,
      ...(pieces ? [pieces] : []),
    ].join(" · ") + ` — ${content}${missing}`
  );
}

/**
 * Uma camada desenhada no palco.
 *
 * Serve aos dois tipos: no único desenha o alvo sozinho, no completo desenha
 * cada camada da peça e recebe `onSelect`. É o mesmo desenho nos dois casos de
 * propósito — se o completo desenhasse diferente, calibrar em um e conferir no
 * outro deixaria de valer.
 */
function StageArt({
  item,
  box,
  selected,
  opacity,
  guides,
  onSelect,
}: {
  item: StageItem;
  box: CollarTransform;
  selected: boolean;
  opacity: number;
  guides: boolean;
  onSelect?: () => void;
}) {
  const layer = item.ref?.layer;
  const clickable = onSelect !== undefined;

  // O texto vive em SVG na MESMA unidade de x/y (% do container), como na
  // loja: calibrar em um sistema de coordenadas e desenhar em outro devolveria
  // uma posição certa aqui e errada lá.
  if (layer?.kind === "TEXT") {
    const sample = sampleFor(item.ref);
    return (
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        // O <svg> ocupa o palco inteiro: deixá-lo clicável faria ele engolir
        // os cliques de TODAS as camadas abaixo. Quem recebe o clique é o
        // próprio texto, logo abaixo.
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <text
          x={box.x}
          y={box.y + box.width}
          textAnchor="middle"
          fill={layer.color ?? "#132a4d"}
          fontFamily={layer.fontFamily ?? "inherit"}
          fontSize={box.width}
          fontWeight={layer.fontWeight ?? 700}
          letterSpacing={layer.letterSpacing ?? 0}
          opacity={opacity}
          transform={box.rotation ? `rotate(${box.rotation} ${box.x} ${box.y})` : undefined}
          {...(layer.outlineColor
            ? {
                stroke: layer.outlineColor,
                strokeWidth: layer.outlineWidth ?? 1,
                paintOrder: "stroke",
              }
            : {})}
          // `all` pega o miolo das letras também: acertar o vazado de um "10"
          // pelo traço seria sorte.
          style={clickable ? { pointerEvents: "all", cursor: "pointer" } : undefined}
          {...(onSelect ? { onClick: onSelect } : {})}
        >
          {layer.uppercase ? sample.toUpperCase() : sample}
        </text>
        {selected && guides && (
          <rect
            x={box.x - box.width}
            y={box.y}
            width={box.width * 2}
            height={box.width * 1.25}
            fill="none"
            stroke="hsl(var(--accent))"
            strokeWidth={0.3}
            strokeDasharray="1 1"
            className="pointer-events-none"
          />
        )}
      </svg>
    );
  }

  return (
    <div
      className={cn("absolute", clickable && "cursor-pointer")}
      style={{
        left: `${box.x}%`,
        top: `${box.y}%`,
        width: `${box.width}%`,
        transform: `translate(-50%, 0) rotate(${box.rotation}deg)`,
        transformOrigin: "50% 0",
        opacity,
        outline: guides
          ? selected
            ? "2px solid hsl(var(--accent))"
            : clickable
              ? "1px dashed hsl(var(--muted-foreground))"
              : "1px dashed hsl(var(--accent))"
          : undefined,
      }}
      {...(onSelect ? { onClick: onSelect } : {})}
    >
      {item.src ? (
        <img
          src={item.src}
          alt={item.label}
          draggable={false}
          className="block h-auto w-full select-none"
        />
      ) : (
        <div className="grid h-16 w-full place-items-center rounded-lg border border-dashed border-accent text-[10px] font-bold text-accent">
          ARTE DO CLIENTE
        </div>
      )}
      {selected && guides && (
        <>
          <span className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-accent/70" />
          <span className="pointer-events-none absolute top-1/2 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent" />
        </>
      )}
    </div>
  );
}
